import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import { Toast } from '../components/ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import EmployeeDetailModal from './EmployeeDetailModal';
import Pagination from '../components/ui/Pagination';
import {
  Search, Download, FileText, Trash2, User, X, Filter,
  Users, ChevronDown, AlertTriangle, Clock, CheckCircle
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { parseISO, format } from 'date-fns';
import { useLoader } from '../contexts/LoaderContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

// ── Debounce hook ──────────────────────────────────────────────────────────
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ── Utilitaires ────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return '—';
  try { return format(parseISO(d), 'dd/MM/yyyy'); } catch { return d; }
};

// ── Calcul statut contrat ──────────────────────────────────────────────────
const getContractStatus = (emp, alerteJours = 30, modeDemarrage = 'manuel') => {
  if (!emp.contract_type_nom) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Période d'essai en cours
  if (emp.trial_period_end_date) {
    const trialEnd = new Date(emp.trial_period_end_date);
    const daysLeft = Math.ceil((trialEnd - today) / 86400000);
    if (daysLeft >= 0) {
      // Pendant l'essai — on n'affiche PAS le décompte contrat
      return {
        type: 'trial',
        label: `Essai rénouvelée - ${daysLeft}j`,
        color: '#b08d1a', bg: '#fef9c3',
        urgent: daysLeft <= 7
      };
    } else {
      // Essai terminé
      if (modeDemarrage === 'manuel') {
        // Mode manuel → badge alerte orange, pas de décompte
        return {
          type: 'trial_ended',
          label: 'Essai terminé - à traiter',
          color: '#ea580c', bg: '#ffedd5',
          urgent: true,
          highlight: true // pour colorer toute la ligne
        };
      }
      // Mode automatique → on passe au décompte contrat normalement
    }
  }

  // Décompte contrat
  if (emp.contract_end_date) {
    const d = new Date(emp.contract_end_date);
    const days = Math.ceil((d - today) / 86400000);
    if (days < 0)           return { type: 'expired',   label: 'Expiré',            color: '#dc2626', bg: '#fee2e2', urgent: true };
    if (days <= alerteJours) return { type: 'expiring',  label: `${days}j restants`, color: '#ea580c', bg: '#ffedd5', urgent: true };
    return                          { type: 'ok',        label: `${days}j restants`, color: '#16a34a', bg: '#dcfce7', urgent: false };
  }

  return null;
};

// ── Composant principal ────────────────────────────────────────────────────
const EmployeesList = () => {
  const { showLoader, hideLoader } = useLoader();

  // Data
  const [allEmployees, setAllEmployees]                   = useState([]);
  const [contractTypes, setContractTypes]                 = useState([]);
  const [statsDepts, setStatsDepts]                       = useState([]);
  const [alerteExpirationJours, setAlerteExpirationJours] = useState(30);
  const [modeDemarrage, setModeDemarrage]                 = useState('manuel');
  const [loading, setLoading]                             = useState(true);
  const [error, setError]                                 = useState(null);

  // Filtres
  const [searchInput, setSearchInput]                         = useState('');
  const [filterDept, setFilterDept]                           = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterContractType, setFilterContractType]           = useState('');
  const [filterExpiry, setFilterExpiry]                       = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // UI
  const [showPdfDropdown, setShowPdfDropdown]               = useState(false);
  const [contractDropdownOpen, setContractDropdownOpen]     = useState(false);
  const contractDropdownRef = useRef(null);
  const pdfDropdownRef      = useRef(null);

  // Modal
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen]       = useState(false);
  const [confirmDialog, setConfirmDialog]       = useState({ isOpen: false, employee: null, isLoading: false });

  // Toast
  const [toastInfo, setToastInfo] = useState(null);
  const showToast = (message, type = 'success') => setToastInfo({ message, type });


  // Feature flag contrats
const { isEnabled: contratsEnabled } = useFeatureFlag('contrats_employes');

  // ── Chargement initial ────────────────────────────────────────────────
  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      showLoader();
      setLoading(true);

     const apiCalls = [
      api.employees.getAll({ page: 1, limit: 9999, search: '', department: '', status: '' }),
      api.employees.getDepartmentStats(),
    ];

    if (contratsEnabled) {
      apiCalls.push(api.contractTypes.getAll());
      apiCalls.push(api.parametreContrat.get());
    }

    const results = await Promise.all(apiCalls);
    const empRes = results[0];
    const statsRes = results[1];
    const ctRes = contratsEnabled ? results[2] : null;
    const contratRes = contratsEnabled ? results[3] : null;

    setAllEmployees(empRes.data || []);
  if (statsRes.success) setStatsDepts(statsRes.data);
  if (contratsEnabled && ctRes?.success) setContractTypes(ctRes.data);
  if (contratsEnabled && contratRes?.success) {
    setAlerteExpirationJours(contratRes.data.alerte_expiration_jours || 30);
    setModeDemarrage(contratRes.data.mode_demarrage_contrat || 'manuel');
  }


    } catch (e) {
      setError(e.message);
      showToast(`Erreur : ${e.message}`, 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // ── Fermer dropdowns au clic extérieur ───────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (contractDropdownRef.current && !contractDropdownRef.current.contains(e.target)) setContractDropdownOpen(false);
      if (pdfDropdownRef.current      && !pdfDropdownRef.current.contains(e.target))      setShowPdfDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Reset page quand les filtres changent
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, filterDept, filterStatus, filterContractType, filterExpiry]);

  // ── Filtrage local (useMemo) ──────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = allEmployees;
    const today = new Date(); today.setHours(0, 0, 0, 0);

    if (debouncedSearch.trim()) {
      const t = debouncedSearch.toLowerCase();
      list = list.filter(e =>
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(t) ||
        (e.email || '').toLowerCase().includes(t) ||
        (e.position || '').toLowerCase().includes(t) ||
        (e.badge_number || '').toLowerCase().includes(t)
      );
    }
    if (filterDept)         list = list.filter(e => e.department === filterDept);
    const statusToApply = filterStatus || 'active';
      list = list.filter(e => e.status === statusToApply);
    if (filterContractType) list = list.filter(e => String(e.contract_type_id) === String(filterContractType));

    if (filterExpiry === 'expiring_soon') list = list.filter(e => {
      if (!e.contract_end_date) return false;
      // Exclure si encore en période d'essai
      if (e.trial_period_end_date && new Date(e.trial_period_end_date) >= today) return false;
      const days = Math.ceil((new Date(e.contract_end_date) - today) / 86400000);
      return days >= 0 && days <= alerteExpirationJours;
    });
    if (filterExpiry === 'expired') list = list.filter(e => {
      if (!e.contract_end_date) return false;
      return new Date(e.contract_end_date) < today;
    });
    if (filterExpiry === 'trial') list = list.filter(e => {
      if (!e.trial_period_end_date) return false;
      return new Date(e.trial_period_end_date) >= today;
    });
    if (filterExpiry === 'trial_ended') list = list.filter(e => {
      if (!e.trial_period_end_date) return false;
      return new Date(e.trial_period_end_date) < today;
    });

    return list;
  }, [allEmployees, debouncedSearch, filterDept, filterStatus, filterContractType, filterExpiry, alerteExpirationJours]);

  // Pagination locale
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated  = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const hasFilters = debouncedSearch || filterDept || filterStatus || filterContractType || filterExpiry;

  // Compteurs
  const todayMs = new Date().setHours(0, 0, 0, 0);

  const activeCount   = allEmployees.filter(e => e.status === 'active').length;
  const inactiveCount = allEmployees.filter(e => e.status === 'inactive').length;
  const expiringSoon = allEmployees.filter(e => {
    if (!e.contract_end_date) return false;
    if (e.trial_period_end_date && new Date(e.trial_period_end_date) >= new Date(todayMs)) return false;
    const d = Math.ceil((new Date(e.contract_end_date) - todayMs) / 86400000);
    return d >= 0 && d <= alerteExpirationJours;
  }).length;
  const expired     = allEmployees.filter(e => e.contract_end_date && new Date(e.contract_end_date) < new Date(todayMs)).length;
  const onTrial     = allEmployees.filter(e => e.trial_period_end_date && new Date(e.trial_period_end_date) >= new Date(todayMs)).length;
  const trialEnded  = allEmployees.filter(e => {
    if (!e.trial_period_end_date) return false;
    return new Date(e.trial_period_end_date) < new Date(todayMs);
  }).length;

  // ── Actions ───────────────────────────────────────────────────────────
  const handleViewDetails = async (emp) => {
    try {
      const res = await api.employees.getOne(emp.id);
      setSelectedEmployee(res);
      setIsDetailsOpen(true);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleUpdateEmployee = (updated) => {
    setAllEmployees(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
    setSelectedEmployee(updated);
  };

  const handleDeleteClick   = (emp) => setConfirmDialog({ isOpen: true, employee: emp, isLoading: false });
  const handleDeleteConfirm = async () => {
    setConfirmDialog(p => ({ ...p, isLoading: true }));
    try {
      await api.employees.delete(confirmDialog.employee.id);
      showToast(`${confirmDialog.employee.first_name} ${confirmDialog.employee.last_name} supprimé`);
      setConfirmDialog({ isOpen: false, employee: null, isLoading: false });
      loadAll();
    } catch {
      showToast('Erreur lors de la suppression', 'error');
      setConfirmDialog(p => ({ ...p, isLoading: false }));
    }
  };

  const handleExportExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(filtered.map(e => ({
        'Prénom': e.first_name, 'Nom': e.last_name, 'Email': e.email,
        'Téléphone': e.phone, 'Poste': e.position, 'Département': e.department,
        "Date d'embauche": formatDate(e.hire_date), 'Salaire': e.salary,
        'Type contrat': e.contract_type_nom || '', 'Statut': e.status
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Employés');
      XLSX.writeFile(wb, `HoraPilot_Employes_${new Date().toLocaleDateString()}.xlsx`);
      showToast('Export Excel réussi');
    } catch { showToast("Erreur export Excel", 'error'); }
  };

  const handleDownloadPDF = async (type = 'all', value = '') => {
    try {
      showToast('Génération PDF...', 'loading');
      await api.employees.telechargerRapportPDF(type, value);
      showToast('PDF téléchargé !');
    } catch { showToast('Erreur PDF', 'error'); }
  };

  if (loading) return null;

  return (
    <div className="space-y-5">

      {/* ── STATS ─────────────────────────────────────────────────────── */}
      <div className={`grid grid-cols-2 gap-4 ${contratsEnabled ? 'md:grid-cols-4' : 'md:grid-cols-1'}`}>
        {[
          { label: 'Total employés',   value: activeCount,                                             icon: Users,         color: '#448696', bg: '#f0f9fb' },
          ...(contratsEnabled ? [
            { label: 'Contrats définis', value: allEmployees.filter(e => e.contract_type_id).length, icon: CheckCircle,   color: '#16a34a', bg: '#dcfce7' },
            { label: 'Expirent bientôt', value: expiringSoon,                                         icon: AlertTriangle, color: '#ea580c', bg: '#ffedd5' },
            { label: "Période d'essai",  value: onTrial,                                              icon: Clock,         color: '#b08d1a', bg: '#fef9c3' },
          ] : [])
        ].map(({ label, value, icon: Icon, color, bg }) => (




          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
              <Icon size={22} style={{ color }} />
            </div>
            <div>
              <p className="text-2xl font-black text-gray-900">{value}</p>
              <p className="text-xs font-semibold text-gray-500 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── BARRE RECHERCHE + EXPORTS ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#448696' }} />
            <input
              type="text"
              placeholder="Rechercher par nom, email, poste, badge..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full pl-11 pr-10 py-3 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none transition-all"
              onFocus={e => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(68,134,150,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 text-gray-400">
                <X size={15} />
              </button>
            )}
          </div>
          <button onClick={handleExportExcel}
            className="px-5 py-3 font-semibold rounded-xl text-sm flex items-center gap-2 hover:opacity-90 shadow-sm text-gray-900"
            style={{ backgroundColor: '#fed774' }}>
            <Download size={17} /> Excel
          </button>
          <div ref={pdfDropdownRef} className="relative">
            <button onClick={() => setShowPdfDropdown(p => !p)}
              className="px-5 py-3 font-semibold rounded-xl text-sm flex items-center gap-2 hover:opacity-90 shadow-sm text-white"
              style={{ backgroundColor: '#448696' }}>
              <FileText size={17} /> PDF
              <ChevronDown size={15} style={{ transform: showPdfDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </button>
            {showPdfDropdown && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-50 min-w-[200px] overflow-hidden">
                <button onClick={() => { handleDownloadPDF('all'); setShowPdfDropdown(false); }}
                  className="w-full px-4 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <Users size={15} style={{ color: '#448696' }} /> Tous les employés
                </button>
                {statsDepts.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase border-t border-gray-100">Par département</div>
                    {statsDepts.map(s => (
                      <button key={s.department} onClick={() => { handleDownloadPDF('department', s.department); setShowPdfDropdown(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-between">
                        <span>{s.department}</span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#44869620', color: '#448696' }}>{s.count}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── FILTRES ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
          <Filter size={13} /> Filtres
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Départements */}
          {statsDepts.map(s => (
            <button key={s.department}
              onClick={() => setFilterDept(f => f === s.department ? '' : s.department)}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
              style={{
                backgroundColor: filterDept === s.department ? '#448696' : 'white',
                color: filterDept === s.department ? 'white' : '#6b7280',
                borderColor: filterDept === s.department ? '#448696' : '#e5e7eb'
              }}>
              {s.department} <span className="opacity-70">{s.count}</span>
            </button>
          ))}

        {statsDepts.length > 0 && <div className="w-px bg-gray-200 self-stretch" />}

{/* Type contrat dropdown - CONDITIONNEL */}
{contratsEnabled && (
  <div ref={contractDropdownRef} className="relative">
    <button onClick={() => setContractDropdownOpen(p => !p)}
      className="px-3 py-1.5 bg-white border-2 rounded-xl font-semibold text-xs transition-all flex items-center gap-1.5"
      style={{
        borderColor: contractDropdownOpen || filterContractType ? '#448696' : '#e5e7eb',
        color: filterContractType ? '#448696' : '#6b7280',
        boxShadow: contractDropdownOpen ? '0 0 0 3px rgba(68,134,150,0.1)' : 'none'
      }}>
      {contractTypes.find(ct => String(ct.id) === String(filterContractType))?.nom || 'Type de contrat'}
      <ChevronDown size={13} style={{ transform: contractDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
    </button>
    {contractDropdownOpen && (
      <div className="absolute top-full mt-1 left-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50 min-w-[180px] max-h-48 overflow-y-auto">
        <button onClick={() => { setFilterContractType(''); setContractDropdownOpen(false); }}
          className="w-full px-3 py-2.5 text-left text-sm text-gray-400 font-medium hover:bg-gray-50 border-b border-gray-50">
          Tous les types
        </button>
        {contractTypes.map(ct => (
          <button key={ct.id} onClick={() => { setFilterContractType(String(ct.id)); setContractDropdownOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
            style={{ backgroundColor: String(filterContractType) === String(ct.id) ? '#f0f9fb' : undefined }}>
            <div className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: String(filterContractType) === String(ct.id) ? '#448696' : '#d1d5db' }} />
            <span className="text-sm font-medium" style={{ color: String(filterContractType) === String(ct.id) ? '#448696' : '#374151' }}>
              {ct.nom}
            </span>
          </button>
        ))}
      </div>
    )}
  </div>
)}

{/* Filtres expiration + essai - CONDITIONNEL */}
{contratsEnabled && [
  { value: 'expiring_soon', label: 'Expire bientôt',      count: expiringSoon },
  { value: 'expired',       label: 'Expirés',              count: expired },
  { value: 'trial',         label: "Période d'essai",      count: onTrial },
  ...(modeDemarrage === 'manuel' ? [{ value: 'trial_ended', label: 'Essai terminé',  count: trialEnded }] : []),
].map(({ value, label, count }) => (
  <button key={value}
    onClick={() => setFilterExpiry(p => p === value ? '' : value)}
    className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
    style={{
      backgroundColor: filterExpiry === value ? '#448696' : 'white',
      color: filterExpiry === value ? 'white' : '#6b7280',
      borderColor: filterExpiry === value ? '#448696' : '#e5e7eb'
    }}>
    {label} <span className="opacity-70 ml-1">{count}</span>
  </button>
))}


        {/* Statut */}
          {[
            { value: 'active',   label: 'Actifs',   count: activeCount },
            { value: 'inactive', label: 'Inactifs', count: inactiveCount }
          ].map(({ value, label, count }) => (
            <button key={value}
              onClick={() => setFilterStatus(p => p === value ? '' : value)}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
              style={{
                backgroundColor: filterStatus === value ? '#448696' : 'white',
                color: filterStatus === value ? 'white' : '#6b7280',
                borderColor: filterStatus === value ? '#448696' : '#e5e7eb'
              }}>
              {label} <span className="opacity-70 ml-1">{count}</span>
            </button>
          ))}

          {/* Effacer */}
          {hasFilters && (
            <button onClick={() => { setSearchInput(''); setFilterDept(''); setFilterStatus(''); setFilterContractType(''); setFilterExpiry(''); }}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1 border border-red-200 text-red-500 hover:bg-red-50 transition-all">
              <X size={13} /> Effacer tout
            </button>
          )}
        </div>

      </div>

      {/* ── TABLEAU ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        <div className="px-6 py-4 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-600">
            <span className="text-gray-900 font-black">{filtered.length}</span> employé{filtered.length > 1 ? 's' : ''}
            {hasFilters && <span className="text-gray-400"> · filtré{filtered.length > 1 ? 's' : ''}</span>}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#f8fafb', borderBottom: '1px solid #f0f2f5' }}>
                {['Employé', 'Poste', 'Contact', 'Département', 'Entrée', ...(contratsEnabled ? ['Contrat'] : []), 'Actions'].map(h => (
                  <th key={h} className={`p-4 text-xs font-bold text-gray-500 uppercase tracking-wide ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <Users size={40} className="mx-auto mb-3 text-gray-200" />
                    <p className="text-sm font-semibold text-gray-400">Aucun employé trouvé</p>
                    {hasFilters && <p className="text-xs text-gray-400 mt-1">Essayez de modifier vos filtres</p>}
                  </td>
                </tr>
              ) : paginated.map((emp) => {
                const status = getContractStatus(emp, alerteExpirationJours, modeDemarrage);
                const isTrialEnded = status?.type === 'trial_ended';

                return (
                  <tr key={emp.id}
                    className="border-b border-gray-50 transition-colors"
                    style={{ backgroundColor: isTrialEnded ? '#fff7ed' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = isTrialEnded ? '#ffedd5' : '#f8fbfc'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = isTrialEnded ? '#fff7ed' : 'transparent'}>

                    {/* Employé */}
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                          style={{ backgroundColor: isTrialEnded ? '#ea580c' : '#448696' }}>
                          <User size={18} className="text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{emp.first_name} {emp.last_name}</p>
                          {emp.badge_number && <p className="text-xs text-gray-400 font-medium">Badge : {emp.badge_number}</p>}
                        </div>
                      </div>
                    </td>

                    {/* Poste */}
                    <td className="p-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold"
                        style={{ backgroundColor: '#fed77430', color: '#92650a' }}>
                        {emp.position || '—'}
                      </span>
                    </td>

                    {/* Contact */}
                    <td className="p-4">
                      <p className="text-sm font-medium text-gray-700">{emp.email}</p>
                      <p className="text-xs text-gray-400">{emp.phone}</p>
                    </td>

                    {/* Département */}
                    <td className="p-4">
                      <span className="text-sm font-semibold text-gray-600">{emp.department || '—'}</span>
                    </td>

                    {/* Date entrée */}
                    <td className="p-4">
                      <span className="text-sm text-gray-500 font-medium">{formatDate(emp.hire_date)}</span>
                    </td>


                    {/* Contrat */}
                  {/* Contrat - CONDITIONNEL */}
                    {contratsEnabled && (
                      <td className="p-4">
                        <div className="space-y-1.5">
                          {emp.contract_type_nom ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold"
                              style={{ backgroundColor: '#44869615', color: '#2d6a7f' }}>
                              {emp.contract_type_nom}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Non défini</span>
                          )}

                          {status && (
                            <div className="flex items-center gap-1.5">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold"
                                style={{ backgroundColor: status.bg, color: status.color }}>
                                {status.label}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                    )}



                    {/* Actions */}
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleViewDetails(emp)}
                          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:opacity-80 shadow-sm"
                          style={{ backgroundColor: '#448696' }} title="Voir détails">
                          <FileText size={15} className="text-white" />
                        </button>
                        <button onClick={() => handleDeleteClick(emp)}
                          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:opacity-80 shadow-sm"
                          style={{ backgroundColor: '#fed774' }} title="Supprimer">
                          <Trash2 size={15} className="text-gray-800" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          total={filtered.length}
          limit={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
          itemLabel="employé"
        />
      </div>

      {/* ── MODALS ────────────────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, employee: null, isLoading: false })}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'employé"
        description={confirmDialog.employee ? `Supprimer ${confirmDialog.employee.first_name} ${confirmDialog.employee.last_name} ? Cette action est irréversible.` : ''}
        type="danger" confirmText="Supprimer" cancelText="Annuler"
        isLoading={confirmDialog.isLoading}
      />

      <EmployeeDetailModal
        employee={selectedEmployee}
        isOpen={isDetailsOpen}
        onClose={() => { setIsDetailsOpen(false); setSelectedEmployee(null); }}
        onUpdate={handleUpdateEmployee}
        onShowToast={({ message, type }) => showToast(message, type)}
      />

      {toastInfo && <Toast message={toastInfo.message} type={toastInfo.type} onClose={() => setToastInfo(null)} />}
    </div>
  );
};

export default EmployeesList;